import type { UseQueryResult } from '@tanstack/react-query';
import type { TRPCClientErrorLike } from '@trpc/client';
import type { AnyRouter } from '@trpc/server';
import { AlertCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Spinner } from './ui/spinner';

export type QueryStateHandlerProps<TData> = {
  query: UseQueryResult<TData, TRPCClientErrorLike<AnyRouter>>;
  emptyMessage: string;
  children: (data: TData) => ReactNode;
};

export const QueryStateHandler = <TData,>({
  query,
  emptyMessage,
  children,
}: QueryStateHandlerProps<TData>): React.ReactElement => {
  const { t } = useTranslation('records');

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner className="size-6" />
      </div>
    );
  }

  if (query.isError) {
    const errorMessage =
      query.error instanceof Error
        ? query.error.message
        : typeof query.error === 'object' && query.error !== null && 'message' in query.error
          ? String((query.error as { message: unknown }).message)
          : t('failedToLoadRecords');
    return (
      <Alert variant="destructive">
        <AlertCircle className="size-4" />
        <AlertTitle>{t('errorLoadingRecords')}</AlertTitle>
        <AlertDescription>{errorMessage}</AlertDescription>
      </Alert>
    );
  }

  if (!query.data || (Array.isArray(query.data) && query.data.length === 0)) {
    return (
      <Alert>
        <AlertCircle className="size-4" />
        <AlertTitle>{emptyMessage}</AlertTitle>
      </Alert>
    );
  }

  return <>{children(query.data)}</>;
};
